import { useEffect, useState, type FormEvent } from "react";

import { api, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

/**
 * Choosing your own password, from a link (IDN-F-021).
 *
 * Open to anybody holding the link, because the person using it has no
 * account yet. The link is the credential: a signature over the staff id,
 * good once and for seven days.
 *
 * Nobody ever sets somebody else's password. Before this screen runs, the
 * account carries a hash that matches nothing, which is why signing in says
 * the account is not set up rather than that the password is wrong.
 */

type State =
  | { name: "reading" }
  | { name: "broken"; why: string }
  | { name: "choosing"; full_name: string; email: string; organization: string }
  | { name: "done"; email: string };

export function AcceptInvitation() {
  const [state, setState] = useState<State>({ name: "reading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Read straight from the address bar rather than through the router: this
  // screen is reached from an email, not from inside the portal.
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  useEffect(() => {
    if (!token) {
      setState({ name: "broken", why: "This link is missing its invitation." });
      return;
    }
    let live = true;

    void (async () => {
      const { data, error: failed } = await api.POST("/auth/staff/invitation", {
        body: { token },
      });
      if (!live) return;
      if (failed || !data) {
        setState({ name: "broken", why: readError(failed).message });
        return;
      }
      setState({
        name: "choosing",
        full_name: data.full_name ?? "",
        email: data.email ?? "",
        organization: data.organization_name ?? "",
      });
    })();

    return () => {
      live = false;
    };
  }, [token]);

  async function choose(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    const again = String(form.get("again") ?? "");

    if (password !== again) {
      setError("Those two do not match.");
      return;
    }

    setBusy(true);
    setError(null);
    const { data, error: failed } = await api.POST(
      "/auth/staff/invitation/accept",
      { body: { token, password } },
    );
    setBusy(false);

    if (failed || !data) {
      setError(readError(failed).message);
      return;
    }
    setState({ name: "done", email: data.email ?? "" });
  }

  return (
    <div className="plat-signin">
      <div className="card plat-signin-card">
        <p className="signin-wordmark">Healtrics</p>

        {state.name === "reading" && <p className="empty">Reading your invitation…</p>}

        {state.name === "broken" && (
          <>
            <h1 className="signin-title">This link will not work</h1>
            <p className="lead">{state.why}</p>
            <p className="muted">
              Links last seven days and work once. Ask whoever added you for
              another, or sign in if you have already chosen a password.
            </p>
            <a className="link" href="/signin">
              Go to sign in
            </a>
          </>
        )}

        {state.name === "choosing" && (
          <form onSubmit={choose}>
            <h1 className="signin-title">Welcome, {state.full_name}</h1>
            <p className="lead">
              You have been added to {state.organization}. Choose a password.
              Nobody else has ever set one for this account, and nobody else
              will see the one you choose.
            </p>
            <p className="muted">
              You will sign in as <code>{state.email}</code>
            </p>

            <label htmlFor="password">Your password</label>
            <div className="field-with-action">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="field-action"
                onClick={() => setShowPassword((on) => !on)}
                aria-pressed={showPassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <label htmlFor="again">Type it again</label>
            <input
              id="again"
              name="again"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
            />

            <ErrorNote message={error} />
            <button type="submit" className="wide" disabled={busy}>
              {busy ? "Saving…" : "Set my password"}
            </button>

            <p className="muted">
              Next you will set up an authenticator app. Both are needed every
              time you sign in.
            </p>
          </form>
        )}

        {state.name === "done" && (
          <>
            <h1 className="signin-title">That is set</h1>
            <p className="lead">
              Sign in as <code>{state.email}</code>. You will set up an
              authenticator app on the way through, which takes one scan.
            </p>
            <a className="link" href="/signin">
              Sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
